import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { Badge, EmptyState, ErrorState, Modal, PageHead } from '../../components/ui';

interface MailAccount {
  id: string;
  label: string;
  address: string;
  displayName: string | null;
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
  imapUser: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  inboxFolder: string;
  sentFolder: string;
  isActive: boolean;
  isShared: boolean;
  status: 'IDLE' | 'SYNCING' | 'ERROR';
  lastError: string | null;
  lastSyncAt: string | null;
  unread: number;
  /** توقيع يُذيَّل تلقائيًّا في كل رسالة تخرج من هذا الصندوق. */
  signatureHtml: string | null;
}

/**
 * إعدادات مزوّدين شائعين.
 *
 * أكثر ما يعطّل الربط ليس كلمة المرور بل خادم أو منفذ خاطئ. الاختيار من
 * قائمة يزيل هذا المصدر كله، ويبقي الإدخال اليدوي لمن يستضيف بريده بنفسه.
 */
const PRESETS: Record<
  string,
  { label: string; imapHost: string; smtpHost: string; note?: string }
> = {
  custom: { label: 'إعداد يدوي', imapHost: '', smtpHost: '' },
  gmail: {
    label: 'Gmail',
    imapHost: 'imap.gmail.com',
    smtpHost: 'smtp.gmail.com',
    note: 'يتطلب تفعيل التحقق بخطوتين ثم إنشاء "كلمة مرور تطبيق" واستعمالها هنا بدل كلمة المرور العادية.',
  },
  outlook: {
    label: 'Outlook أو Microsoft 365',
    imapHost: 'outlook.office365.com',
    smtpHost: 'smtp.office365.com',
    note: 'قد يحتاج تفعيل IMAP من إعدادات الحساب، ومنفذ SMTP رقم 587 مع تشفير STARTTLS.',
  },
  zoho: { label: 'Zoho Mail', imapHost: 'imap.zoho.com', smtpHost: 'smtp.zoho.com' },
  hostinger: {
    label: 'Hostinger',
    imapHost: 'imap.hostinger.com',
    smtpHost: 'smtp.hostinger.com',
  },
  yandex: { label: 'Yandex', imapHost: 'imap.yandex.com', smtpHost: 'smtp.yandex.com' },
};

const STATUS: Record<MailAccount['status'], { label: string; kind: 'success' | 'info' | 'danger' }> =
  {
    IDLE: { label: 'متصل', kind: 'success' },
    SYNCING: { label: 'جارٍ المزامنة', kind: 'info' },
    ERROR: { label: 'خطأ', kind: 'danger' },
  };

export function MailSettings() {
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<MailAccount | 'new' | null>(null);

  const canEdit = atLeast('MANAGER');

  const accounts = useQuery({
    queryKey: ['mail', 'accounts'],
    queryFn: () => api<{ items: MailAccount[] }>('/mail/accounts'),
  });

  const sync = useMutation({
    mutationFn: (id: string) => api(`/mail/accounts/${id}/sync`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mail'] });
    },
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['mail'] });
    setEditing(null);
  };

  return (
    <div className="page">
      <PageHead
        title="البريد الإلكتروني"
        subtitle="اربط بريد شركتك ليصل إلى النظام ويُرسَل منه"
        actions={
          canEdit && (
            <button className="btn btn-primary" onClick={() => setEditing('new')}>
              <Icon name="plus" size={15} />
              ربط صندوق
            </button>
          )
        }
      />

      {accounts.isError && (
        <ErrorState error={accounts.error} onRetry={() => void accounts.refetch()} />
      )}

      {sync.isError && (
        <div className="alert alert-danger mb-4">
          {sync.error instanceof Error ? sync.error.message : 'تعذّرت المزامنة.'}
        </div>
      )}

      {accounts.data?.items.length === 0 && (
        <EmptyState
          title="لا صناديق مربوطة"
          text="الربط عبر IMAP وSMTP يعمل مع أي مزوّد بريد. بعده تصل رسائل عملائك إلى النظام مرتبطة بملفاتهم، وتُرسل الردود من العنوان نفسه."
          action={
            canEdit && (
              <button className="btn btn-primary" onClick={() => setEditing('new')}>
                ربط صندوق
              </button>
            )
          }
        />
      )}

      <div className="col" style={{ gap: 12 }}>
        {accounts.data?.items.map((a) => (
          <div key={a.id} className="card">
            <div className="card-head">
              <div className="col" style={{ gap: 2 }}>
                <div className="row" style={{ gap: 8 }}>
                  <h2 className="card-title">{a.label}</h2>
                  <Badge kind={STATUS[a.status].kind}>{STATUS[a.status].label}</Badge>
                  {!a.isActive && <Badge kind="neutral">موقوف</Badge>}
                  {!a.isShared && <Badge kind="info">خاص</Badge>}
                </div>
                <span className="tiny mute num" dir="ltr">
                  {a.address}
                </span>
              </div>

              <div className="row" style={{ gap: 6 }}>
                <button
                  className="btn btn-sm"
                  onClick={() => sync.mutate(a.id)}
                  disabled={sync.isPending || !a.isActive}
                >
                  <Icon name="refresh" size={14} />
                  مزامنة الآن
                </button>
                {canEdit && (
                  <button
                    className="btn btn-sm btn-icon btn-ghost"
                    aria-label={`تعديل ${a.label}`}
                    onClick={() => setEditing(a)}
                  >
                    <Icon name="edit" size={15} />
                  </button>
                )}
              </div>
            </div>

            <div className="card-body">
              {a.lastError && (
                <div className="alert alert-danger mb-4">
                  آخر خطأ: {a.lastError}
                </div>
              )}

              <div className="form-grid">
                <div className="field">
                  <span className="label">خادم الوارد</span>
                  <span className="num" dir="ltr">
                    {a.imapHost}:{a.imapPort}
                  </span>
                </div>
                <div className="field">
                  <span className="label">خادم الصادر</span>
                  <span className="num" dir="ltr">
                    {a.smtpHost}:{a.smtpPort}
                  </span>
                </div>
                <div className="field">
                  <span className="label">آخر مزامنة</span>
                  <span>{a.lastSyncAt ? dateTime(a.lastSyncAt) : 'لم تتم بعد'}</span>
                </div>
                <div className="field">
                  <span className="label">غير مقروءة</span>
                  <span className="num">{a.unread}</span>
                </div>
              </div>

              <SignatureEditor account={a} canEdit={canEdit} onSaved={refresh} />
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <AccountModal
          account={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

/* ========================================================================== */

function AccountModal({
  account,
  onClose,
  onSaved,
}: {
  account: MailAccount | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [preset, setPreset] = useState('custom');
  const [form, setForm] = useState({
    label: account?.label ?? '',
    address: account?.address ?? '',
    displayName: account?.displayName ?? '',
    imapHost: account?.imapHost ?? '',
    imapPort: String(account?.imapPort ?? 993),
    imapSecure: account?.imapSecure ?? true,
    imapUser: account?.imapUser ?? '',
    imapSecret: '',
    smtpHost: account?.smtpHost ?? '',
    smtpPort: String(account?.smtpPort ?? 465),
    smtpSecure: account?.smtpSecure ?? true,
    smtpUser: account?.smtpUser ?? '',
    smtpSecret: '',
    inboxFolder: account?.inboxFolder ?? 'INBOX',
    sentFolder: account?.sentFolder ?? 'Sent',
    isShared: account?.isShared ?? true,
    isActive: account?.isActive ?? true,
  });

  const set = (key: string, value: unknown) => setForm((f) => ({ ...f, [key]: value }));

  const applyPreset = (key: string) => {
    setPreset(key);
    const p = PRESETS[key];
    if (!p || key === 'custom') return;
    setForm((f) => ({ ...f, imapHost: p.imapHost, smtpHost: p.smtpHost }));
  };

  const payload = () => ({
    label: form.label,
    address: form.address,
    displayName: form.displayName || undefined,
    imapHost: form.imapHost,
    imapPort: Number(form.imapPort) || 993,
    imapSecure: form.imapSecure,
    // اسم المستخدم يساوي العنوان عند أغلب المزوّدين، فيُملأ تلقائيًا.
    imapUser: form.imapUser || form.address,
    imapSecret: form.imapSecret,
    smtpHost: form.smtpHost,
    smtpPort: Number(form.smtpPort) || 465,
    smtpSecure: form.smtpSecure,
    smtpUser: form.smtpUser || form.imapUser || form.address,
    smtpSecret: form.smtpSecret || undefined,
    inboxFolder: form.inboxFolder,
    sentFolder: form.sentFolder,
    isShared: form.isShared,
  });

  const test = useMutation({
    mutationFn: () =>
      api<{ folders: string[]; suggestedSent: string }>('/mail/accounts/test', {
        method: 'POST',
        body: payload(),
      }),
    onSuccess: (result) => {
      // المزوّدون يسمّون مجلد المرسَل بأسماء مختلفة، فيُقترح ما وُجد فعلًا.
      if (result.suggestedSent) set('sentFolder', result.suggestedSent);
    },
  });

  const save = useMutation({
    mutationFn: () =>
      account
        ? api(`/mail/accounts/${account.id}`, {
            method: 'PATCH',
            body: { ...payload(), isActive: form.isActive },
          })
        : api('/mail/accounts', { method: 'POST', body: payload() }),
    onSuccess: onSaved,
  });

  const remove = useMutation({
    mutationFn: () => api(`/mail/accounts/${account!.id}`, { method: 'DELETE' }),
    onSuccess: onSaved,
  });

  const valid =
    form.label.trim().length >= 2 &&
    /.+@.+\..+/.test(form.address) &&
    form.imapHost.length > 2 &&
    form.smtpHost.length > 2 &&
    (account || form.imapSecret.length > 0);

  const note = PRESETS[preset]?.note;

  return (
    <Modal
      title={account ? `تعديل ${account.label}` : 'ربط صندوق بريد'}
      onClose={onClose}
      wide
      footer={
        <>
          {account && (
            <button
              className="btn btn-danger me-auto"
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              فصل الصندوق
            </button>
          )}
          <button
            className="btn"
            type="button"
            onClick={() => test.mutate()}
            disabled={!valid || test.isPending}
          >
            {test.isPending ? 'جارٍ الفحص' : 'فحص الاتصال'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}
      {test.isError && (
        <div className="alert alert-danger mb-4">
          {test.error instanceof Error ? test.error.message : 'تعذّر الاتصال.'}
        </div>
      )}
      {test.isSuccess && (
        <div className="alert alert-info mb-4">
          الاتصال ناجح. وُجد {test.data.folders.length} مجلدًا على الخادم، ومجلد المرسَل المقترح
          هو <span className="num">{test.data.suggestedSent}</span>.
        </div>
      )}

      <div className="form-grid">
        <div className="field">
          <label className="label" htmlFor="m-preset">
            المزوّد
          </label>
          <select
            id="m-preset"
            className="select"
            value={preset}
            onChange={(e) => applyPreset(e.target.value)}
          >
            {Object.entries(PRESETS).map(([key, p]) => (
              <option key={key} value={key}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label req" htmlFor="m-label">
            اسم الصندوق
          </label>
          <input
            id="m-label"
            className="input"
            value={form.label}
            onChange={(e) => set('label', e.target.value)}
            placeholder="بريد المبيعات"
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="m-address">
            عنوان البريد
          </label>
          <input
            id="m-address"
            className="input num"
            type="email"
            dir="ltr"
            value={form.address}
            onChange={(e) => set('address', e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="m-display">
            الاسم الظاهر للمستقبِل
          </label>
          <input
            id="m-display"
            className="input"
            value={form.displayName}
            onChange={(e) => set('displayName', e.target.value)}
            placeholder="فريق مبيعات الشركة"
          />
        </div>
      </div>

      {note && <div className="alert alert-warning mt-4">{note}</div>}

      <h4 className="section-title">الوارد (IMAP)</h4>
      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="m-imap-host">
            الخادم
          </label>
          <input
            id="m-imap-host"
            className="input num"
            dir="ltr"
            value={form.imapHost}
            onChange={(e) => set('imapHost', e.target.value)}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="m-imap-port">
            المنفذ
          </label>
          <input
            id="m-imap-port"
            className="input num"
            type="number"
            value={form.imapPort}
            onChange={(e) => set('imapPort', e.target.value)}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="m-imap-user">
            اسم المستخدم
          </label>
          <input
            id="m-imap-user"
            className="input num"
            dir="ltr"
            value={form.imapUser}
            onChange={(e) => set('imapUser', e.target.value)}
            placeholder={form.address || 'نفس عنوان البريد'}
          />
        </div>
        <div className="field">
          <label className={`label${account ? '' : ' req'}`} htmlFor="m-imap-pass">
            كلمة المرور
          </label>
          <input
            id="m-imap-pass"
            className="input"
            type="password"
            dir="ltr"
            value={form.imapSecret}
            onChange={(e) => set('imapSecret', e.target.value)}
            placeholder={account ? 'اتركها فارغة للإبقاء على الحالية' : ''}
          />
        </div>
      </div>

      <h4 className="section-title">الصادر (SMTP)</h4>
      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="m-smtp-host">
            الخادم
          </label>
          <input
            id="m-smtp-host"
            className="input num"
            dir="ltr"
            value={form.smtpHost}
            onChange={(e) => set('smtpHost', e.target.value)}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="m-smtp-port">
            المنفذ
          </label>
          <input
            id="m-smtp-port"
            className="input num"
            type="number"
            value={form.smtpPort}
            onChange={(e) => set('smtpPort', e.target.value)}
          />
          <span className="tiny mute">465 للتشفير المباشر، و587 مع STARTTLS.</span>
        </div>
        <div className="field span-2">
          <label className="label" htmlFor="m-smtp-pass">
            كلمة مرور مختلفة للصادر
          </label>
          <input
            id="m-smtp-pass"
            className="input"
            type="password"
            dir="ltr"
            value={form.smtpSecret}
            onChange={(e) => set('smtpSecret', e.target.value)}
            placeholder="اتركها فارغة لاستعمال كلمة مرور الوارد"
          />
        </div>
      </div>

      <h4 className="section-title">المجلدات والخيارات</h4>
      <div className="form-grid">
        <div className="field">
          <label className="label" htmlFor="m-inbox">
            مجلد الوارد
          </label>
          <input
            id="m-inbox"
            className="input num"
            dir="ltr"
            value={form.inboxFolder}
            onChange={(e) => set('inboxFolder', e.target.value)}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="m-sent">
            مجلد المرسَل
          </label>
          <input
            id="m-sent"
            className="input num"
            dir="ltr"
            value={form.sentFolder}
            onChange={(e) => set('sentFolder', e.target.value)}
          />
          <span className="tiny mute">تُحفظ فيه نسخة مما يُرسَل من النظام.</span>
        </div>
      </div>

      <div className="col mt-4">
        <label className="row" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.isShared}
            onChange={(e) => set('isShared', e.target.checked)}
          />
          <span className="small">مشترك مع الفريق — بريد قسم لا بريد شخص</span>
        </label>

        {account && (
          <label className="row" style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
            />
            <span className="small">نشط ويُزامَن دوريًا</span>
          </label>
        )}
      </div>

      <div className="alert alert-info mt-4">
        كلمات المرور تُشفَّر قبل حفظها ولا تظهر بعد ذلك في أي شاشة. الرسائل تبقى على خادم بريدك
        ولا تُحذف منه؛ ما يُحفظ هنا نسخة للعرض والربط بملفات العملاء.
      </div>
    </Modal>
  );
}

/* ========================================================================== */

/**
 * توقيع الصندوق — كتوقيع Gmail.
 *
 * يُحرَّر هنا مرة ويُذيَّل تلقائيًّا في كل رسالة تخرج من الصندوق، بدل أن
 * يعيد كل موظف كتابة اسمه ورقمه في كل رسالة — أو ينساهما.
 *
 * والصورة (شعار أو توقيع مرسوم) تدخل `data:` مضغوطةً بعرضٍ لا يتجاوز
 * شاشة هاتف — التوقيع يُقرأ في ذيل رسالة لا في معرض صور.
 */
function SignatureEditor({
  account,
  canEdit,
  onSaved,
}: {
  account: MailAccount;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const file = useRef<HTMLInputElement>(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* يُزرع مرة لكل صندوق: contentEditable يملك محتواه بعدها. */
  useEffect(() => {
    if (ref.current) ref.current.innerHTML = account.signatureHtml ?? '';
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.id]);

  const save = useMutation({
    mutationFn: () => {
      const html = (ref.current?.innerHTML ?? '').trim();
      const empty = html === '' || html === '<br>' || html === '<div><br></div>';
      return api(`/mail/accounts/${account.id}`, {
        method: 'PATCH',
        body: { signatureHtml: empty ? null : html },
      });
    },
    onSuccess: () => {
      setDirty(false);
      setError(null);
      onSaved();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const exec = (cmd: string) => {
    ref.current?.focus();
    if (cmd === 'createLink') {
      const url = window.prompt('عنوان الرابط:', 'https://');
      if (!url || url === 'https://') return;
      document.execCommand(cmd, false, url);
    } else {
      document.execCommand(cmd, false);
    }
    setDirty(true);
  };

  const insertImage = async (chosen: File) => {
    setError(null);
    if (!chosen.type.startsWith('image/')) {
      setError('اختر ملف صورة.');
      return;
    }
    try {
      const dataUrl = await shrinkForSignature(chosen);
      ref.current?.focus();
      document.execCommand('insertImage', false, dataUrl);
      setDirty(true);
    } catch {
      setError('تعذّرت قراءة الصورة.');
    } finally {
      if (file.current) file.current.value = '';
    }
  };

  return (
    <div className="mt-4">
      <div className="row mb-2" style={{ gap: 'var(--sp-2)', alignItems: 'center' }}>
        <h3 className="section-title" style={{ margin: 0 }}>
          التوقيع
        </h3>
        <span className="tiny mute">يُذيَّل تلقائيًّا في كل رسالة تُرسل من هذا الصندوق</span>
      </div>

      {error && <div className="alert alert-danger mb-2">{error}</div>}

      <div
        ref={ref}
        className="sig-editor"
        contentEditable={canEdit}
        dir="auto"
        data-placeholder="مثال: الاسم — الوظيفة — الهاتف"
        aria-label={`توقيع ${account.label}`}
        onInput={() => setDirty(true)}
      />

      {canEdit && (
        <div className="row mt-2" style={{ gap: 'var(--sp-1)', alignItems: 'center' }}>
          <span className="composer-tools" role="toolbar" aria-label="تنسيق التوقيع">
            {(
              [
                ['bold', 'عريض'],
                ['italic', 'مائل'],
                ['underline', 'تسطير'],
                ['createLink', 'رابط'],
                ['removeFormat', 'إزالة التنسيق'],
              ] as const
            ).map(([cmd, label]) => (
              <button
                key={cmd}
                type="button"
                className="composer-tool"
                title={label}
                aria-label={label}
                onMouseDown={(e) => {
                  e.preventDefault();
                  exec(cmd);
                }}
              >
                <Icon
                  name={
                    cmd === 'bold'
                      ? 'bold'
                      : cmd === 'italic'
                        ? 'italic'
                        : cmd === 'underline'
                          ? 'underline'
                          : cmd === 'createLink'
                            ? 'link'
                            : 'eraser'
                  }
                  size={15}
                />
              </button>
            ))}

            {/* صورة: شعار الشركة أو توقيع إلكتروني مرسوم. */}
            <button
              type="button"
              className="composer-tool"
              title="إدراج صورة — شعار أو توقيع إلكتروني"
              aria-label="إدراج صورة"
              onMouseDown={(e) => {
                e.preventDefault();
                file.current?.click();
              }}
            >
              <Icon name="plus" size={15} />
            </button>
          </span>

          <input
            ref={file}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={(e) => {
              const chosen = e.target.files?.[0];
              if (chosen) void insertImage(chosen);
            }}
          />

          <button
            className="btn btn-sm btn-primary"
            style={{ marginInlineStart: 'auto' }}
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ التوقيع'}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * تصغير صورة التوقيع.
 *
 * العرض يُحدّ بمئتين وأربعين بكسلًا — يكفي شعارًا أو توقيعًا مرسومًا.
 * وPNG يبقى PNG حفاظًا على الشفافية، وسواه يخرج JPEG.
 */
async function shrinkForSignature(input: File): Promise<string> {
  const bitmap = await createImageBitmap(input);
  try {
    const scale = Math.min(1, 240 / bitmap.width);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return input.type === 'image/png'
      ? canvas.toDataURL('image/png')
      : canvas.toDataURL('image/jpeg', 0.85);
  } finally {
    bitmap.close();
  }
}
